import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Ship, Leaf, Home } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      // Buyers land on shipments list; admin lands on dashboard
      nav(u?.role === "buyer" ? "/app/shipments" : "/app", { replace: true });
    } catch (e2) {
      const msg = formatApiErrorDetail(e2?.response?.data?.detail) || e2.message;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 relative">
      {/* Floating Home button */}
      <Link
        to="/"
        data-testid="login-home-btn"
        className="absolute top-4 right-4 z-30 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-[#6B0F1A] border border-[#6B0F1A]/20 hover:border-[#6B0F1A] text-xs font-semibold rounded-full px-3.5 py-2 shadow-md hover:shadow-lg transition-all"
      >
        <Home className="w-3.5 h-3.5" /> Home
      </Link>

      {/* Left panel */}
      <div
        className="relative hidden md:flex flex-col justify-between p-10 text-white"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(107,15,26,0.92), rgba(34,68,29,0.92)), url(https://images.unsplash.com/photo-1691591765923-3bd6f12f4209?auto=format&fit=crop&w=1600&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Link to="/" className="flex items-center gap-3 hover:opacity-90" data-testid="login-logo-home">
          <div className="bg-white rounded-xl px-3 py-2 shadow-md">
            <img src="/traderepubliq-logo.svg" alt="TradeRepubliq" className="h-7" />
          </div>
        </Link>
        <div className="space-y-4">
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Moving Agri Products.<br />
            From Origin to Market.
          </h1>
          <p className="text-white/80 max-w-md">
            Execution-led trade management for agri-commodities — guided, step-by-step, and audit-ready.
          </p>
          <div className="flex gap-6 pt-6">
            <Stat value="15+" label="Origin Countries" />
            <Stat value="99.2%" label="QC Pass Rate" />
            <Stat value="90%+" label="On-Time Delivery" />
          </div>
        </div>
        <div className="text-xs text-white/60">© TradeRepubliq · Operations Suite</div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center items-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex md:hidden items-center justify-between mb-8">
            <Link to="/" data-testid="login-logo-home-mobile">
              <img src="/traderepubliq-logo.svg" alt="TradeRepubliq" className="h-7" />
            </Link>
          </div>

          <div className="inline-flex items-center gap-2 bg-[#F4E8E9] text-[#6B0F1A] text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
            <Leaf className="w-3.5 h-3.5" /> Agri-Commodities Trade Operations
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1F1F1F]">
            Sign in
          </h2>
          <p className="text-stone-500 mt-2 text-sm">Manage shipments of agri products from origin to delivery.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-4 py-3 text-[#1F1F1F] focus:bg-white focus:ring-2 focus:ring-[#A8323F]/25 focus:border-[#A8323F] outline-none"
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-4 py-3 text-[#1F1F1F] focus:bg-white focus:ring-2 focus:ring-[#A8323F]/25 focus:border-[#A8323F] outline-none"
              />
            </Field>
            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg" data-testid="login-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full bg-[#6B0F1A] hover:bg-[#56121C] disabled:opacity-60 text-white font-semibold rounded-xl py-3 px-6 active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <Ship className="w-4 h-4" />
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-xs text-stone-400">
            Need access? Contact your TradeRepubliq administrator to receive your buyer login credentials.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-2xl font-extrabold">{value}</div>
      <div className="text-white/70 text-xs">{label}</div>
    </div>
  );
}
